import { Box, Typography, Chip, IconButton, Tooltip, Collapse } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import TypingIntro from './TypingIntros';

function DedupeAgentLog({ data, onRefresh }) {
  const scrollRef = useRef(null);
  const [expanded, setExpanded] = useState({}); // track expanded state per log

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data]);

  const levelColors = {
    INFO: '#2196f3',
    SUCCESS: '#4caf50',
    ERROR: '#f44336',
    WARN: '#ff9800',
    WINNER: '#9c27b0',
  };

  const toggleExtra = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <Box>
      <TypingIntro />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          fontWeight: 'bold',
        }}
      >
        <Box>🧠 Logs Viewer</Box>
        <Tooltip title="Refresh Logs">
          <IconButton onClick={onRefresh} size="small" sx={{ color: '#555' }}>
            <RotateLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          backgroundColor: '#f9f9f9',
          color: '#000',
          padding: 2,
          borderRadius: 2,
          fontFamily: 'monospace',
          border: '1px solid #ddd',
          height: '60vh',
          overflowY: 'auto',
        }}
      >
        {data?.map((log, idx) => {
          let parsed;
          try {
            parsed = JSON.parse(log.message);
          } catch (e) {
            parsed = { message: log.message };
          }

          const level = parsed.level?.toUpperCase() || 'INFO';
          const chipColor = levelColors[level] || '#4caf50';

          return (
            <Box
              key={idx}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              {/* Meta info row */}
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip
                  label={level}
                  size="small"
                  sx={{
                    backgroundColor: chipColor,
                    color: '#fff',
                    fontWeight: 'bold',
                    height: 20,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {parsed.timestamp}
                </Typography>
              </Box>

              {/* Log message */}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                {parsed.message}
              </Typography>

              {/* Extra data with collapse */}
              {parsed.extra && (
                <Box>
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer', mb: 0.5 }}
                    onClick={() => toggleExtra(idx)}
                  >
                    {expanded[idx] ? '▼ Hide Extra' : '▶ Show Extra'}
                  </Typography>
                  <Collapse in={expanded[idx]}>
                    <Box
                      sx={{
                        backgroundColor: '#f5f5f5',
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        fontFamily: 'monospace',
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(parsed.extra, null, 2)}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          );
        })}
        <div ref={scrollRef} />
      </Box>
    </Box>
  );
}

export default DedupeAgentLog;
